// lib/auth.ts
const API_BASE = 'https://api.vehidhi.com';

export interface AuthResponse {
  access_token?: string;
  token_type?: string;
  expires_in?: number;
  message?: string;
  errors?: any;
}

export interface User {
  id?: number;
  name?: string;
  email?: string;
  avatar?: string;
  [key: string]: any;
}

// Store tokens
export const setAuthToken = (token: string) => {
  localStorage.setItem('access_token', token);
};

export const getAuthToken = (): string | null => {
  return localStorage.getItem('access_token');
};

export const removeAuthToken = () => {
  localStorage.removeItem('access_token');
};

export const setUser = (user: User) => {
  localStorage.setItem('user', JSON.stringify(user));
};

export const getUser = (): User | null => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

export const removeUser = () => {
  localStorage.removeItem('user');
};

// Check if user is logged in
export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

// Login function
export const login = async (email: string, password: string): Promise<AuthResponse> => {
  try {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (response.ok && data.access_token) {
      setAuthToken(data.access_token);
      // Fetch user after successful login
      const user = await getCurrentUser();
      if (user) {
        setUser(user);
      }
    }

    return data;
  } catch (error) {
    console.error('Login error:', error);
    return { message: 'Network error. Please try again.' };
  }
};

// Get current user from API
export const getCurrentUser = async (): Promise<User | null> => {
  const token = getAuthToken();
  if (!token) return null;

  try {
    const response = await fetch(`${API_BASE}/auth/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const user = await response.json();
      setUser(user);
      return user;
    } else {
      // Token might be expired
      removeAuthToken();
      removeUser();
      return null;
    }
  } catch (error) {
    console.error('Error fetching user:', error);
    return null;
  }
};

// Logout
export const logout = () => {
  removeAuthToken();
  removeUser();
};

// Initialize auth on app load
export const initAuth = async (): Promise<User | null> => {
  const token = getAuthToken();
  if (!token) return null;

  const user = await getCurrentUser();
  return user;
};